import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../services/api';
import { getAllEmployees, putAllEmployees, deleteEmployee, getEmployeeFetchTime, setEmployeeFetchTime } from '../services/employeeDB';
import type { AppDispatch, RootState } from './index';
import type { EmployeeListItem, EmployeeRecord } from '../types/employees';

interface EmployeesState {
  list: EmployeeListItem[];
  isLoading: boolean;
  error: string | null;
}

const initialState: EmployeesState = {
  list: [],
  isLoading: false,
  error: null,
};

const employeesSlice = createSlice({
  name: 'employees',
  initialState,
  reducers: {
    setEmployeeList(state, action: PayloadAction<EmployeeListItem[]>) {
      state.list = action.payload;
    },
    upsertEmployeeListItem(state, action: PayloadAction<EmployeeListItem>) {
      const idx = state.list.findIndex((e) => e.employeeUUID === action.payload.employeeUUID);
      if (idx !== -1) {
        state.list[idx] = action.payload;
      } else {
        state.list.push(action.payload);
      }
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const { setEmployeeList, upsertEmployeeListItem, setLoading, setError } = employeesSlice.actions;

function extractLatestValue(v: any): string {
  if (Array.isArray(v)) {
    if (v.length === 0) return '';
    // History entries are sorted by date by the backend; last is latest.
    const latest = v[v.length - 1];
    return latest && latest.value != null ? String(latest.value) : '';
  }
  return v != null ? String(v) : '';
}

export function toListItem(r: EmployeeRecord): EmployeeListItem {
  return {
    employeeUUID: String(r['employeeUUID'] ?? ''),
    employeeInternalId: String(r['employeeInternalId'] ?? ''),
    biometricId: extractLatestValue(r['biometricId']),
    employeeName: extractLatestValue(r['employeeName']),
    updated_at: String(r['updated_at'] ?? ''),
  };
}

export interface SyncEmployeesCallbacks {
  // Called once after the outdated-UUID GET completes, with the total number of
  // steps for the entire sync (1 UUID fetch + n chunks).
  onTotalDiscovered: (syncTotal: number) => void;
  // Called after each completed step (1-indexed): 1 = UUID fetch done, 2+ = chunk done.
  onStepComplete: (syncCompleted: number) => void;
}

const CHUNK_SIZE = 200;

export const syncEmployees =
  (callbacks: SyncEmployeesCallbacks) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    let totalDiscovered = false;
    let knownSyncTotal = 1;
    try {
      const { selectedUnit } = getState().auth;
      const dbKey = selectedUnit
        ? `cranberry-employees-${selectedUnit.tenant}-${selectedUnit.solution}-${selectedUnit.unit}`
        : 'cranberry-employees';

      // Read fetchTime synchronously — never blocks.
      const fetchTime = getEmployeeFetchTime(dbKey);

      // Fire the API call immediately — do NOT wait for IDB before this.
      const outdatedPromise = apiService.getOutdatedEmployeeUUIDs(fetchTime);

      // Read IDB in parallel. If it hangs or fails, ignore it — the API call is already in flight.
      getAllEmployees(dbKey)
        .then((cached) => {
          if (cached.length > 0) {
            const now = new Date();
            const cutoffDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 3, 1));
            const yyyy = cutoffDate.getUTCFullYear();
            const mm = String(cutoffDate.getUTCMonth() + 1).padStart(2, '0');
            const cutoffMonthStr = `${yyyy}-${mm}`;

            const validCached: EmployeeRecord[] = [];
            for (const r of cached) {
              const termDate = extractLatestValue(r['terminationDate']);
              if (termDate && termDate.length >= 7 && termDate.substring(0, 7) < cutoffMonthStr) {
                deleteEmployee(dbKey, String(r['employeeUUID'] ?? '')).catch(() => {});
              } else {
                validCached.push(r);
              }
            }

            if (validCached.length > 0) {
              dispatch(setEmployeeList(validCached.map(toListItem)));
            } else {
              dispatch(setEmployeeList([]));
            }
          }
        })
        .catch(() => {});

      const outdatedResponse = await outdatedPromise;
      const uuids = outdatedResponse.data ?? [];

      const totalChunks = Math.ceil(uuids.length / CHUNK_SIZE);
      const syncTotal = 1 + totalChunks; // 1 UUID GET + n chunk POSTs
      callbacks.onTotalDiscovered(syncTotal);
      totalDiscovered = true;
      knownSyncTotal = syncTotal;
      callbacks.onStepComplete(1); // UUID GET done

      for (let i = 0; i < totalChunks; i++) {
        const chunk = uuids.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const chunkResponse = await apiService.getEmployeeList(chunk);
        const records = chunkResponse.data ?? [];

        putAllEmployees(dbKey, records).catch(() => {}); // fire-and-forget — IDB write must not block progress
        records.forEach((r) => dispatch(upsertEmployeeListItem(toListItem(r))));

        callbacks.onStepComplete(2 + i); // chunk i done
      }

      setEmployeeFetchTime(dbKey, Date.now());
    } catch (error: any) {
      dispatch(setError(error?.message ?? 'Failed to sync employees'));
      // Always unblock the loading bar regardless of where the error occurred.
      if (!totalDiscovered) {
        callbacks.onTotalDiscovered(1);
      }
      callbacks.onStepComplete(knownSyncTotal);
    } finally {
      dispatch(setLoading(false));
    }
  };

export default employeesSlice.reducer;
